import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Bars3Icon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { useUpdateFirmMutation } from '@/services/api/firmsService';
import {
  Firm,
  IntakePreferenceSection,
  QualificationCriteria,
} from '@/services/types/firms-types';

import { AddSectionDialog } from './AddSectionDialog';
import { DeleteSectionDialog } from './DeleteSectionDialog';
import { EditSectionDialog } from './EditSectionDialog';
import { QualificationCriteriaSection } from './QualificationCriteriaSection';

type SortableSection = IntakePreferenceSection & { clientId: string };

const generateSectionClientId = (): string => {
  if (
    typeof globalThis.crypto !== 'undefined' &&
    globalThis.crypto?.randomUUID
  ) {
    return globalThis.crypto.randomUUID();
  }
  return `section-${Math.random().toString(36).slice(2, 11)}`;
};

const withClientIds = (
  sections: IntakePreferenceSection[],
): SortableSection[] =>
  sections.map((section) => ({
    ...section,
    clientId: generateSectionClientId(),
  }));

const stripClientIds = (
  sections: SortableSection[],
): IntakePreferenceSection[] =>
  sections.map(({ title, content }) => ({ title, content }));

interface SortableSectionCardProps {
  section: SortableSection;
  index: number;
  onEdit: (updated: IntakePreferenceSection) => void;
  onDelete: () => void;
}

const SortableSectionCard: React.FC<SortableSectionCardProps> = ({
  section,
  index,
  onEdit,
  onDelete,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.clientId });

  const transformStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={transformStyle}
      className={`border border-gray-200 rounded-lg p-4 bg-white transition-shadow ${
        isDragging ? 'shadow-lg ring-1 ring-blue-200' : ''
      }`}
      {...attributes}
    >
      <div className="flex items-start justify-between mb-3 gap-3">
        <div className="flex items-start gap-3">
          <button
            type="button"
            {...listeners}
            className="mt-1 flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 text-gray-400 hover:bg-gray-100 cursor-grab active:cursor-grabbing"
            aria-label={`Reorder section ${section.title}`}
          >
            <Bars3Icon className="h-4 w-4" />
          </button>
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500">
              Section {index + 1}
            </p>
            <h3 className="text-base font-semibold text-gray-900">
              {section.title}
            </h3>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <EditSectionDialog section={section} onSave={onEdit} />
          <DeleteSectionDialog section={section} onDelete={onDelete} />
        </div>
      </div>
      <p className="text-sm text-gray-700 whitespace-pre-line">
        {section.content || (
          <span className="text-gray-400 italic">No content</span>
        )}
      </p>
    </div>
  );
};

interface IntakePreferencesProps {
  firm: Firm;
}

export const IntakePreferences: React.FC<IntakePreferencesProps> = ({
  firm,
}) => {
  const [updateFirm, { isLoading }] = useUpdateFirmMutation();

  const serverSections = firm.intake_preferences?.sections;
  const [sections, setSections] = useState<SortableSection[]>(() =>
    withClientIds(serverSections ?? []),
  );

  useEffect(() => {
    setSections(withClientIds(serverSections ?? []));
  }, [serverSections]);

  const persistSections = async (
    nextSections: SortableSection[],
    successMessage: string,
    errorMessage: string,
  ): Promise<void> => {
    const previousSections = sections;
    setSections(nextSections);

    const result = await updateFirm({
      id: firm.id,
      intake_preferences: {
        ...firm.intake_preferences,
        sections: stripClientIds(nextSections),
      },
    });

    if ('error' in result) {
      toast.error(errorMessage);
      setSections(previousSections);
      return;
    }

    toast.success(successMessage);
  };

  const handleCriteriaChange = async (
    criteria: QualificationCriteria,
  ): Promise<void> => {
    const result = await updateFirm({
      id: firm.id,
      intake_preferences: {
        ...firm.intake_preferences,
        sections: firm.intake_preferences?.sections ?? [],
        qualification_criteria: criteria,
      },
    });

    if ('error' in result) {
      toast.error('Failed to update qualification criteria');
      return;
    }

    toast.success('Qualification criteria updated');
  };

  const handleAddSection = async (
    newSection: IntakePreferenceSection,
  ): Promise<void> => {
    await persistSections(
      [...sections, { ...newSection, clientId: generateSectionClientId() }],
      'Section added successfully',
      'Failed to add section',
    );
  };

  const handleEditSection = async (
    clientId: string,
    updatedSection: IntakePreferenceSection,
  ): Promise<void> => {
    const updatedSections = sections.map((section) =>
      section.clientId === clientId
        ? { ...section, ...updatedSection }
        : section,
    );

    await persistSections(
      updatedSections,
      'Section updated successfully',
      'Failed to update section',
    );
  };

  const handleDeleteSection = async (clientId: string): Promise<void> => {
    const updatedSections = sections.filter(
      (section: SortableSection) => section.clientId !== clientId,
    );

    await persistSections(
      updatedSections,
      'Section deleted successfully',
      'Failed to delete section',
    );
  };

  const handleDragEnd = async ({
    active,
    over,
  }: DragEndEvent): Promise<void> => {
    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = sections.findIndex(
      (section) => section.clientId === active.id,
    );
    const newIndex = sections.findIndex(
      (section) => section.clientId === over.id,
    );

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const updatedSections = arrayMove(sections, oldIndex, newIndex);

    await persistSections(
      updatedSections,
      'Section order updated',
      'Failed to reorder sections',
    );
  };

  return (
    <div className="space-y-6">
      {/* Qualification Criteria Section */}
      <QualificationCriteriaSection
        criteria={firm.intake_preferences?.qualification_criteria}
        onChange={handleCriteriaChange}
        isLoading={isLoading}
      />

      {/* Guideline Sections */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Intake Guidelines</h2>
          <p className="mt-1 text-sm text-gray-600">
            Add custom guidelines and preferences for intake operators
          </p>
        </div>
        <AddSectionDialog onAdd={handleAddSection} />
      </div>

      {sections.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-gray-300 rounded-lg bg-gray-50">
          <p className="text-gray-600">No intake preferences configured yet.</p>
          <p className="text-sm text-gray-500 mt-1">
            Add a section to get started.
          </p>
        </div>
      ) : (
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sections.map((section) => section.clientId)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-4">
              {sections.map((section, index) => (
                <SortableSectionCard
                  key={section.clientId}
                  section={section}
                  index={index}
                  onEdit={(updated) =>
                    handleEditSection(section.clientId, updated)
                  }
                  onDelete={() => handleDeleteSection(section.clientId)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {isLoading && (
        <div className="fixed bottom-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg">
          Saving...
        </div>
      )}
    </div>
  );
};
